/* daniels-quick-view.js
   Quick view modal for collection page.
   Fetches product data via /products/handle.js and renders
   a popup matching the design: image, color, size, measurements,
   price summary, qty stepper, add to cart.
*/
(function () {
  'use strict';

  /* ══ COLOR-MATCHING HELPERS (same concept as the product page gallery) ══
     An image counts as belonging to a color if its alt text contains that
     color name as a whole word/phrase, with comma support for images
     shared across colors, and disambiguation for overlapping names
     (e.g. "Black" vs "Triple Black"). ── */
  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function colorMatchesAlt(colorLower, altLower) {
    var re = new RegExp('\\b' + escapeRegex(colorLower) + '\\b');
    return re.test(altLower);
  }

  function getAltColorTags(altText) {
    if (!altText) return [];
    return altText.split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
  }

  // Shopify's /products/handle.js endpoint exposes alt text via `media`
  // (each entry has .alt and .src/.preview_image.src). The older `images`
  // array is just plain URL strings with no alt text, so we prefer `media`
  // when it's present and fall back gracefully otherwise.
  function getGalleryImages(product) {
    if (product.media && product.media.length) {
      return product.media
        .map(function (m) {
          var src = m.src || (m.preview_image && m.preview_image.src) || '';
          return { src: src, alt: m.alt || '' };
        })
        .filter(function (m) { return m.src; });
    }
    if (product.images && product.images.length) {
      return product.images.map(function (img) {
        return { src: typeof img === 'string' ? img : (img.src || ''), alt: '' };
      });
    }
    return [];
  }

  function pickImageForColor(product, colorValue, allColorValues) {
    if (!colorValue) return null;
    var images = getGalleryImages(product);
    if (!images.length) return null;

    var cv = colorValue.trim().toLowerCase();
    var moreSpecific = allColorValues
      .map(function (v) { return v.trim().toLowerCase(); })
      .filter(function (other) { return other !== cv && colorMatchesAlt(cv, other); });

    var match = null;
    for (var i = 0; i < images.length; i++) {
      var tags = getAltColorTags(images[i].alt);
      var hit = tags.some(function (tag) {
        return colorMatchesAlt(cv, tag) && !moreSpecific.some(function (other) { return colorMatchesAlt(other, tag); });
      });
      if (hit) { match = images[i]; break; }
    }
    return match ? match.src : null;
  }

  /* ── Create modal HTML ── */
  var modal = document.createElement('div');
  modal.id = 'dlQvOverlay';
  modal.innerHTML = [
    '<div id="dlQvModal">',
      '<button id="dlQvClose" aria-label="Close">&#x2715;</button>',
      '<div id="dlQvBody">',
        '<div id="dlQvImageWrap"><img id="dlQvImage" src="" alt=""></div>',
        '<div id="dlQvInfo">',
          '<h2 id="dlQvTitle"></h2>',
          '<div id="dlQvPrice"></div>',
          '<div id="dlQvOptions"></div>',
          '<div id="dlQvMeasurements">',
            '<div class="dlQv-mfield">',
              '<label class="dlQv-mlabel" for="dlQvWeight">Weight in pounds</label>',
              '<input type="number" id="dlQvWeight" class="dlQv-minput" min="0" step="0.1" placeholder="">',
            '</div>',
            '<div class="dlQv-mfield">',
              '<label class="dlQv-mlabel" for="dlQvHeight">Height in feet</label>',
              '<input type="number" id="dlQvHeight" class="dlQv-minput" min="0" step="0.1" placeholder="">',
            '</div>',
          '</div>',
          '<div id="dlQvSummary">',
            '<div class="dlQv-srow"><span>PRODUCT PRICE:</span><span id="dlQvSumPrice"></span></div>',
            '<div class="dlQv-srow"><span>TOTAL OPTIONS:</span><span>$0</span></div>',
            '<div class="dlQv-srow dlQv-srow--total"><span>ORDER TOTAL:</span><span id="dlQvSumTotal"></span></div>',
          '</div>',
          '<div id="dlQvQtyWrap">',
            '<button type="button" id="dlQvMinus">-</button>',
            '<input type="number" id="dlQvQty" value="1" min="1">',
            '<button type="button" id="dlQvPlus">+</button>',
          '</div>',
          '<button type="button" id="dlQvAtc">ADD TO CART</button>',
          '<a id="dlQvViewFull" href="#">VIEW FULL DETAILS</a>',
        '</div>',
      '</div>',
    '</div>'
  ].join('');
  document.body.appendChild(modal);

  /* ── Styles ── */
  var style = document.createElement('style');
  style.textContent = [
    '#dlQvOverlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:9000;align-items:center;justify-content:center;padding:20px;}',
    '#dlQvOverlay.open{display:flex;}',
    '#dlQvModal{background:#fff;max-width:820px;width:100%;max-height:90vh;overflow-y:auto;position:relative;display:flex;flex-direction:column;}',
    '#dlQvClose{position:absolute;top:14px;right:16px;background:none;border:none;font-size:20px;cursor:pointer;z-index:2;color:#111;line-height:1;}',
    '#dlQvBody{display:grid;grid-template-columns:1fr 1fr;min-height:400px;}',
    '#dlQvImageWrap{background:#f5f5f5;display:flex;align-items:center;justify-content:center;padding:20px;}',
    '#dlQvImage{max-width:100%;max-height:420px;object-fit:contain;display:block;}',
    '#dlQvInfo{padding:28px 24px;display:flex;flex-direction:column;gap:12px;overflow-y:auto;}',
    '#dlQvTitle{font-size:18px;font-weight:600;line-height:1.3;color:#111;margin:0;padding-right:24px;}',
    '#dlQvPrice{font-size:16px;font-weight:600;color:#111;}',
    '#dlQvPrice .qv-compare{font-size:13px;color:#999;text-decoration:line-through;margin-right:6px;}',
    '.dlQv-option-row{display:flex;flex-direction:column;gap:6px;width:100%;margin-bottom:4px;}',
    '.dlQv-option-label{font-size:11px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;color:#111;min-width:48px;}',
    '.dlQv-size-btn{min-width:36px;height:32px;padding:0 8px;border:1px solid #ddd;background:#fff;font-size:11px;font-weight:500;cursor:pointer;color:#111;transition:all 0.15s;}',
    '.dlQv-size-btn:hover{border-color:#111;}',
    '.dlQv-size-btn.active{background:#111;color:#fff;border-color:#111;}',
    '.dlQv-size-btn.unavailable{opacity:0.3;text-decoration:line-through;cursor:not-allowed;}',
    '.dlQv-color-swatch{width:26px;height:26px;border:none;outline:1px solid rgba(0,0,0,0.15);cursor:pointer;box-sizing:border-box;padding:0;background-size:100% 100%;}',
    '.dlQv-color-swatch.active{outline:2px solid #111;outline-offset:2px;}',
    '.dlQv-view-size{font-size:11px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;text-decoration:underline;color:#111;background:none;border:none;cursor:pointer;padding:0;}',
    '#dlQvMeasurements{display:grid;grid-template-columns:1fr 1fr;gap:10px;}',
    '.dlQv-mfield{display:flex;flex-direction:column;gap:4px;}',
    '.dlQv-mlabel{font-size:11px;font-weight:500;color:#111;text-align:center;text-decoration:underline;text-underline-offset:2px;}',
    '.dlQv-minput{width:100%;height:40px;border:1px solid #ddd;padding:0 10px;font-size:13px;outline:none;}',
    '.dlQv-minput:focus{border-color:#111;}',
    '#dlQvSummary{border-top:1px solid #eee;border-bottom:1px solid #eee;padding:8px 0;}',
    '.dlQv-srow{display:flex;justify-content:space-between;padding:4px 0;font-size:11px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;color:#888;}',
    '.dlQv-srow--total{color:#111;font-size:12px;padding-top:8px;margin-top:4px;border-top:1px solid #eee;}',
    '#dlQvQtyWrap{display:flex;align-items:center;border:1px solid #111;width:fit-content;}',
    '#dlQvQtyWrap button{width:34px;height:34px;background:none;border:none;font-size:16px;cursor:pointer;font-family:inherit;}',
    '#dlQvQty{width:40px;height:34px;border:none;border-left:1px solid #111;border-right:1px solid #111;text-align:center;font-size:13px;font-weight:600;outline:none;-moz-appearance:textfield;}',
    '#dlQvQty::-webkit-inner-spin-button,#dlQvQty::-webkit-outer-spin-button{-webkit-appearance:none;}',
    '#dlQvAtc{width:100%;height:48px;background:#111;color:#fff;border:2px solid #111;font-size:12px;font-weight:700;letter-spacing:2px;text-transform:uppercase;cursor:pointer;transition:all 0.2s;}',
    '#dlQvAtc:hover{background:#fff;color:#111;}',
    '#dlQvAtc:disabled{opacity:0.5;cursor:not-allowed;}',
    '#dlQvViewFull{display:block;text-align:center;font-size:11px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;color:#111;text-decoration:underline;text-underline-offset:3px;margin-top:4px;}',
    '@media(max-width:640px){#dlQvBody{grid-template-columns:1fr;}#dlQvImageWrap{max-height:260px;}}'
  ].join('');
  document.head.appendChild(style);

  /* ── State ── */
  var currentProduct = null;
  var selectedOptions = {};
  var currentColorOptionIndex = null; // which option index is the color option, if any

  /* ── Helpers ── */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function findVariant() {
    if (!currentProduct) return null;
    return currentProduct.variants.find(function (v) {
      return v.options.every(function (val, i) {
        return selectedOptions[i] === undefined || selectedOptions[i] === val;
      });
    });
  }

  // All distinct values used for a given option index across every variant
  function getOptionValues(product, optIdx) {
    var seen = {};
    var values = [];
    product.variants.forEach(function (v) {
      var val = v.options[optIdx];
      if (val && !seen[val]) { seen[val] = true; values.push(val); }
    });
    return values;
  }

  function updatePrice() {
    var v = findVariant();
    if (!v) return;
    var priceEl = document.getElementById('dlQvPrice');
    var html = '';
    if (v.compare_at_price && v.compare_at_price > v.price) {
      html += '<span class="qv-compare">' + money(v.compare_at_price) + '</span>';
    }
    html += money(v.price);
    priceEl.innerHTML = html;
    document.getElementById('dlQvSumPrice').textContent = money(v.price);
    document.getElementById('dlQvSumTotal').textContent = money(v.price);

    var qvImg = document.getElementById('dlQvImage');
    function fixUrl(url) {
      if (!url) return '';
      if (url.indexOf('//') === 0) url = 'https:' + url;
      return url;
    }

    var newImgSrc = '';

    // 1. Prefer an alt-tag color match — this is what lets a single shared
    //    photo (alt="Black, White") or a dedicated photo per color work,
    //    independent of which specific variant image Shopify assigned.
    if (currentProduct && currentColorOptionIndex !== null && selectedOptions[currentColorOptionIndex]) {
      var allColors = getOptionValues(currentProduct, currentColorOptionIndex);
      var colorMatch = pickImageForColor(currentProduct, selectedOptions[currentColorOptionIndex], allColors);
      if (colorMatch) newImgSrc = fixUrl(colorMatch);
    }

    // 2. Fall back to the variant's own featured image
    if (!newImgSrc && v.featured_image) {
      newImgSrc = fixUrl(typeof v.featured_image === 'string' ? v.featured_image : v.featured_image.src);
    }

    // 3. Fall back to the product's featured image / first image
    if (!newImgSrc && currentProduct && currentProduct.featured_image) {
      newImgSrc = fixUrl(typeof currentProduct.featured_image === 'string'
        ? currentProduct.featured_image
        : currentProduct.featured_image.src);
    } else if (!newImgSrc && currentProduct && currentProduct.images && currentProduct.images.length > 0) {
      newImgSrc = fixUrl(typeof currentProduct.images[0] === 'string'
        ? currentProduct.images[0]
        : currentProduct.images[0].src);
    }

    if (newImgSrc) qvImg.src = newImgSrc;

    // Update ATC button
    var atc = document.getElementById('dlQvAtc');
    atc.disabled = !v.available;
    atc.textContent = v.available ? 'ADD TO CART' : 'SOLD OUT';
    atc.dataset.variantId = v.id;
  }

  function updateSwatches() {
    if (!currentProduct) return;
    document.querySelectorAll('.dlQv-color-swatch, .dlQv-size-btn').forEach(function (btn) {
      var idx = parseInt(btn.dataset.optionIndex);
      var val = btn.dataset.value;
      // Check if this option+value combo has any available variant
      var testOpts = Object.assign({}, selectedOptions);
      testOpts[idx] = val;
      var exists = currentProduct.variants.some(function (v) {
        return v.options.every(function (opt, i) {
          return testOpts[i] === undefined || testOpts[i] === opt;
        });
      });
      btn.classList.toggle('unavailable', !exists && btn.classList.contains('dlQv-size-btn'));
    });
  }

  /* ── Render product in modal ── */
  function renderProduct(product, productUrl) {
    currentProduct = product;
    selectedOptions = {};
    currentColorOptionIndex = null;

    // Init selected options from first available variant
    var initVariant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
    initVariant.options.forEach(function (val, i) { selectedOptions[i] = val; });

    // Title
    document.getElementById('dlQvTitle').textContent = product.title;

    // Full details link
    document.getElementById('dlQvViewFull').href = productUrl;

    // Options
    var optionsContainer = document.getElementById('dlQvOptions');
    optionsContainer.innerHTML = '';

    product.options.forEach(function (optionRaw, optIdx) {
      // product.options can be strings or objects depending on API version
      var optionName = typeof optionRaw === 'object' ? (optionRaw.name || String(optionRaw)) : String(optionRaw);

      var row = document.createElement('div');
      row.className = 'dlQv-option-row';
      row.style.width = '100%';
      row.style.display = 'flex';
      row.style.flexDirection = 'column';
      row.style.gap = '6px';

      var label = document.createElement('span');
      label.className = 'dlQv-option-label';

      var optLower = optionName.toLowerCase();
      var isColor = optLower === 'color' || optLower === 'colour' || optLower === 'tanuki';
      var isSize  = optLower === 'size' || optLower === 'baby size';

      if (isColor) {
        currentColorOptionIndex = optIdx;
        // Show "Color: [value]" label that updates
        var colorLabel = document.createElement('span');
        colorLabel.className = 'dlQv-option-label';
        colorLabel.id = 'dlQvColorLabel';
        colorLabel.textContent = optionName + ': ' + selectedOptions[optIdx];
        row.appendChild(colorLabel);
      } else {
        label.textContent = optionName;
        row.appendChild(label);
      }

      // Get unique values for this option
      // If option is an object (from .js endpoint), use option.values directly
      var values = [];
      if (typeof optionRaw === 'object' && optionRaw.values) {
        values = optionRaw.values;
      } else {
        var seen = {};
        product.variants.forEach(function (v) {
          var val = v.options[optIdx];
          if (!seen[val]) { seen[val] = true; values.push(val); }
        });
      }

      var btnsWrap = document.createElement('div');
      btnsWrap.style.display = 'flex';
      btnsWrap.style.flexWrap = 'wrap';
      btnsWrap.style.gap = '5px';
      btnsWrap.style.width = '100%';

      values.forEach(function (val) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.optionIndex = optIdx;
        btn.dataset.value = val;

        if (isColor) {
          btn.className = 'dlQv-color-swatch';
          btn.title = val;
          btn.setAttribute('aria-label', val);
          // Look up color in map
          var colorMap = {
            'Black':'#000000','White':'#ffffff','Red':'#cc1020','Blue':'#1a3a8a',
            'Navy Blue':'#001f5b','Green':'#2a7a2a','Olive':'#6b8e23','Grey':'#888888',
            'Gray':'#888888','Brown':'#8B4513','Cognac':'#c07830','Burgundy':'#7b0020',
            'Maroon':'#7b0100','Orange':'#e07020','Yellow':'#ffe020','Hot Pink':'#ff69b4',
            'Baby Pink':'#f4c2c2','Pink':'#e8a5b0','Purple':'#6a0dad','Aqua':'#00bcd4',
            'Beige':'#f5f0dc','Sky Blue':'#87ceeb','Teal':'#008080','Cream':'#fffdd0',
            'Silver':'#c0c0c0','Golden':'#ffd700','Honey':'#ffb347','Turquiose':'#40e0d0',
            'Mint Green':'#98ff98','Lime Green':'#32cd32','Dark Blue':'#00008b',
            'Royal blue':'#4169e1','Ice Blue':'#b0d4f1','Deep Pink':'#ff1493',
            'Orchid':'#da70d6','Lavendar':'#967bb6','Dark Purple':'#4b0082',
            'Tan':'#d2b48c','Whiskey':'#c68642','Multicolor':'#cccccc','Crystal':'#e0e8f0',
            'Natural Red':'#cc3333','Fur':'#c9a87c','Leather':'#8B6914',
            'Chocolate Brown':'#7b3f00','Dark Brown':'#3b1f0e','Hunter Green':'#355e3b',
            'Midnight Blue':'#191970','Steel Blue':'#4682b4','Rustic Brown':'#6b3a2a',
            'Rustic Orange':'#cc5500','Rustic Red':'#8b1a1a','Rustic Green':'#4a6741',
            'Triple Black':'#000000','Pure Black':'#000000'
          };
          btn.style.background = colorMap[val] || '#cccccc';
        } else {
          btn.className = 'dlQv-size-btn';
          btn.textContent = val;
        }

        if (selectedOptions[optIdx] === val) btn.classList.add('active');

        btn.addEventListener('click', function () {
          selectedOptions[optIdx] = val;
          // Update active state for this option group
          btnsWrap.querySelectorAll('button').forEach(function (b) {
            b.classList.toggle('active', b.dataset.value === val);
          });
          // Update color label
          if (isColor) {
            var cl = document.getElementById('dlQvColorLabel');
            if (cl) cl.textContent = optionName + ': ' + val;
          }
          updateSwatches();
          updatePrice();
        });

        btnsWrap.appendChild(btn);
      });

      row.appendChild(btnsWrap);
      optionsContainer.appendChild(row);
    });

    // Set the initial image (uses the same color-match-first logic as
    // every subsequent swatch click, via updatePrice)
    updatePrice();
    updateSwatches();

    // Reset qty
    document.getElementById('dlQvQty').value = 1;
  }

  // Expose render function globally so inline onclick can call it
  window.dlQvRender = function(product, productUrl) {
    renderProduct(product, productUrl);
    openModal();
  };

  /* ── Open / Close modal ── */
  var overlay = document.getElementById('dlQvOverlay');

  function openModal() {
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    overlay.classList.remove('open');
    document.body.style.overflow = '';
    currentProduct = null;
  }

  document.getElementById('dlQvClose').addEventListener('click', closeModal);
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closeModal();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.classList.contains('open')) closeModal();
  });

  /* ── Qty stepper ── */
  document.getElementById('dlQvMinus').addEventListener('click', function () {
    var q = document.getElementById('dlQvQty');
    if (parseInt(q.value) > 1) q.value = parseInt(q.value) - 1;
  });
  document.getElementById('dlQvPlus').addEventListener('click', function () {
    var q = document.getElementById('dlQvQty');
    q.value = parseInt(q.value) + 1;
  });

  /* ── Add to Cart ── */
  document.getElementById('dlQvAtc').addEventListener('click', function () {
    var btn = this;
    var variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    var qty = parseInt(document.getElementById('dlQvQty').value) || 1;
    var weight = document.getElementById('dlQvWeight').value;
    var height = document.getElementById('dlQvHeight').value;

    var properties = {};
    if (weight) properties['Weight in pounds'] = weight;
    if (height) properties['Height in feet'] = height;

    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = 'ADDING…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: qty, properties: properties }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.textContent = 'ADDED ✓';
        setTimeout(function () {
          btn.textContent = original;
          btn.disabled = false;
        }, 1500);
        // Update cart count
        return fetch('/cart.js');
      })
      .then(function (r) { return r && r.json(); })
      .then(function (cart) {
        if (cart) {
          var countEl = document.getElementById('cart-count');
          if (countEl) {
            countEl.textContent = cart.item_count;
            countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
          }
        }
      })
      .catch(function () {
        btn.textContent = 'ERROR – TRY AGAIN';
        btn.disabled = false;
      });
  });

  /* ── Hook into Quick View buttons on the page ── */
  function bindQuickView() {
    document.querySelectorAll('.daniels-quick-view').forEach(function (btn) {
      if (btn._dlQvBound) return;
      btn._dlQvBound = true;

      // Use capture:true so this fires BEFORE any other click handlers
      // (including window.open calls in older collection.js versions)
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // stops ALL other listeners on this element

        var productUrl = btn.dataset.productUrl;
        if (!productUrl) return;

        // Show loading state
        btn.textContent = '...';

        // Fetch product JSON
        var handle = productUrl.replace(/^.*\/products\//, '').split('?')[0];
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.json(); })
          .then(function (product) {
            btn.textContent = 'QUICK VIEW';
            renderProduct(product, productUrl);
            openModal();
          })
          .catch(function () {
            btn.textContent = 'QUICK VIEW';
            window.location.href = productUrl;
          });
      }, true); // capture phase
    });
  }

  // Bind on load
  bindQuickView();

  // Re-bind after Load More adds new cards
  document.addEventListener('daniels:cards:loaded', bindQuickView);

  // Also observe for new cards added to the grid
  var grid = document.getElementById('danielsProductGrid');
  if (grid && window.MutationObserver) {
    var observer = new MutationObserver(function () { bindQuickView(); });
    observer.observe(grid, { childList: true, subtree: false });
  }

  // Expose so collection.js can call it after Load More
  window.dlBindQuickView = bindQuickView;

})();